import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, Target, Heart, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const fadeUp = {
 initial: { opacity: 0, y: 30 },
 animate: { opacity: 1, y: 0 },
 transition: { duration: 0.6 },
};

const values = [
 {
  icon: Target,
  title: "Clarity Over Comfort",
  description: "We would rather give you an uncomfortable truth than a comfortable lie. Clarity. even when it is hard. is what moves you forward.",
 },
 {
  icon: Users,
  title: "Founders First",
  description: "We are not optimizing for our revenue. We are optimizing for your outcomes. If we are not the right fit, we will tell you.",
 },
 {
  icon: Heart,
  title: "Respect for the Journey",
  description: "Building something is hard. We have been there. We bring empathy, not judgment, to every conversation.",
 },
 {
  icon: Lightbulb,
  title: "Learning Over Ego",
  description: "We do not have all the answers. We bring experience and frameworks, but we learn alongside you.",
 },
];

const evolution = [
 {
  era: "The Beginning",
  description: "We started as a services company, taking on projects that paid the bills. We learned a lot. about clients, about projects, about what actually matters.",
 },
 {
  era: "The Realization",
  description: "We noticed a pattern: the best outcomes came when we worked closely with founders, not just executed specs. The worst came when we built without validating.",
 },
 {
  era: "The Pivot",
  description: "We stopped being a general services company. We focused exclusively on founder-led execution. helping founders validate, build, and scale with intention.",
 },
 {
  era: "Today",
  description: "A lean team, focused on quality over quantity. We take on a limited number of founders and give each engagement our full attention.",
 },
];

// Three people collaborating illustration
const illustration = "/images/teamwork.png";

export default function AboutPage() {
 return (
  <div className="flex flex-col">
   {/* Hero */}
   <section className="bg-hero py-16 sm:py-20 lg:py-24">
    <div className="container-narrow">
     <motion.div
      initial="initial"
      animate="animate"
      variants={fadeUp}
      className="text-center"
     >
      <h1 className="heading-display text-foreground mb-6">
       About Let's Build Startup
      </h1>
      <p className="body-large text-muted-foreground max-w-2xl mx-auto">
       A founder-led execution partner for founders who carry real responsibility.
      </p>
     </motion.div>
    </div>
   </section>

   {/* Mission with Illustration */}
   <section className="bg-background py-16 sm:py-20 lg:py-24">
    <div className="container-wide">
     <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      {/* Content */}
      <motion.div
       initial={{ opacity: 0, x: -30 }}
       whileInView={{ opacity: 1, x: 0 }}
       viewport={{ once: true, margin: "-100px" }}
       transition={{ duration: 0.7 }}
      >
       <h2 className="heading-large text-foreground mb-6">What We Stand For</h2>
       
       <div className="bg-card border border-border/50 rounded-xl p-8">
        <blockquote>
         <p className="text-xl lg:text-2xl font-serif text-foreground leading-relaxed mb-5 italic">
          &quot;Founders do not fail because of bad ideas. They fail because clarity comes too late.&quot;
         </p>
         <footer className="text-muted-foreground">
          This is our founding belief. Everything we do flows from it.
         </footer>
        </blockquote>
       </div>
      </motion.div>

      {/* Illustration */}
      <motion.div
       initial={{ opacity: 0, x: 30 }}
       whileInView={{ opacity: 1, x: 0 }}
       viewport={{ once: true, margin: "-100px" }}
       transition={{ duration: 0.7, delay: 0.1 }}
       className="flex justify-center"
      >
       <div className="w-full max-w-sm">
        <img
         src={illustration}
         alt="Team collaboration"
         className="w-full h-auto"
        />
       </div>
      </motion.div>
     </div>
    </div>
   </section>

   {/* Values */}
   <section className="bg-section py-16 sm:py-20 lg:py-24">
    <div className="container-base">
     <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6 }}
      className="text-center mb-12"
     >
      <h2 className="heading-large text-foreground mb-5">Our Values</h2>
      <p className="body-base text-muted-foreground">
       These are not aspirational. They are operational.
      </p>
     </motion.div>

     <div className="grid md:grid-cols-2 gap-6">
      {values.map((value, i) => (
       <motion.div
        key={value.title}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: i * 0.1 }}
       >
        <Card className="card-elevated h-full">
         <CardContent className="p-6">
          <div className="flex items-start gap-4">
           <div className="icon-container flex-shrink-0">
            <value.icon className="h-5 w-5 text-accent" />
           </div>
           <div>
            <h3 className="font-serif text-lg font-medium text-foreground mb-2">{value.title}</h3>
            <p className="text-muted-foreground text-sm">{value.description}</p>
           </div>
          </div>
         </CardContent>
        </Card>
       </motion.div>
      ))}
     </div>
    </div>
   </section>

   {/* Evolution */}
   <section className="bg-background py-16 sm:py-20 lg:py-24">
    <div className="container-narrow">
     <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6 }}
     >
      <h2 className="heading-section text-foreground mb-10 text-center">How We Got Here</h2>
      
      <div className="space-y-0">
       {evolution.map((step, i) => (
        <motion.div
         key={step.era}
         initial={{ opacity: 0, x: -20 }}
         whileInView={{ opacity: 1, x: 0 }}
         viewport={{ once: true }}
         transition={{ duration: 0.5, delay: i * 0.15 }}
         className="flex gap-6 pb-8"
        >
         <div className="flex flex-col items-center">
          <motion.div 
           className="w-3 h-3 rounded-full bg-accent"
           initial={{ scale: 0 }}
           whileInView={{ scale: 1 }}
           viewport={{ once: true }}
           transition={{ delay: i * 0.15, type: "spring" }}
          />
          {i < evolution.length - 1 && <div className="w-px h-full bg-border flex-1 mt-2" />}
         </div>
         <div className="pb-2">
          <h3 className="font-serif text-lg font-medium text-foreground mb-2">{step.era}</h3>
          <p className="text-muted-foreground">{step.description}</p>
         </div>
        </motion.div>
       ))}
      </div>
     </motion.div>
    </div>
   </section>

   {/* Team Philosophy */}
   <section className="bg-section py-12 sm:py-16">
    <div className="container-narrow">
     <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-card border border-border/50 rounded-xl p-8 text-center"
     >
      <h3 className="font-serif text-xl font-medium text-foreground mb-4">Why a Lean Team?</h3>
      <p className="text-muted-foreground max-w-2xl mx-auto">
       We could scale. We choose not to. A lean team means every founder gets senior attention. No junior handoffs. No account managers. When you work with us, you work with us. directly.
      </p>
     </motion.div>
    </div>
   </section>

   {/* CTA */}
   <section className="bg-primary text-primary-foreground py-16 sm:py-20">
    <div className="container-narrow text-center">
     <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
     >
      <h2 className="heading-section mb-5">Want to Know More?</h2>
      <p className="body-base text-primary-foreground/80 mb-6">
       Read the founder story, or reach out directly.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4">
       <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full">
        <Link to="/founder">
         Read the Founder Story
         <ArrowRight className="h-4 w-4 ml-2" />
        </Link>
       </Button>
       <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full">
        <Link to="/contact">Get in Touch</Link>
       </Button>
      </div>
     </motion.div>
    </div>
   </section>
  </div>
 );
}
